import { ReactNode } from "react";
import { motion } from "framer-motion";
import { Check, Lock, ShieldAlert, TrendingUp, AlertTriangle } from "lucide-react";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";
import { getTone, type Tone } from "./types";

export interface LunimarDecisionCardProps {
  realm?: LunimarRealm;
  title: string;
  description?: string;
  /** One-line tradeoff line shown with caution tint. */
  tradeoff?: string;
  /** Headline immediate effect (count, $, % string). */
  immediateEffect?: string;
  /** Risk indicator label. */
  risk?: "Low" | "Medium" | "High";
  /** Reward / upside indicator label. */
  reward?: "Low" | "Medium" | "High";
  /** Optional realm-specific icon (lucide component). */
  icon?: ReactNode;
  selected?: boolean;
  /** Disabled after confirmation. */
  locked?: boolean;
  /** When true, dims the card (not the chosen one). */
  dimmed?: boolean;
  /** Surfaces tone on the selected-state border / glow. */
  tone?: Tone;
  onSelect?: () => void;
  children?: ReactNode;
}

const RISK_COLOR: Record<string, string> = {
  Low: "152 70% 55%",
  Medium: "38 95% 60%",
  High: "0 78% 60%",
};

/**
 * Realm-aware decision card. Replaces plain choice buttons across modules.
 * Shows title + description + tradeoff + risk/reward chips, with selected,
 * hover, dimmed and locked states.
 */
const LunimarDecisionCard = ({
  realm,
  title,
  description,
  tradeoff,
  immediateEffect,
  risk,
  reward,
  icon,
  selected,
  locked,
  dimmed,
  tone = "active",
  onSelect,
  children,
}: LunimarDecisionCardProps) => {
  const t = getTone(tone, realm);
  const accent = realm?.primaryAccent ?? "210 90% 64%";
  const activeAccent = selected ? t.fg : accent;

  return (
    <motion.button
      type="button"
      disabled={locked && !selected}
      onClick={onSelect}
      whileHover={!locked ? { y: -2 } : undefined}
      whileTap={!locked ? { scale: 0.99 } : undefined}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      className={[
        "group relative w-full overflow-hidden rounded-2xl border p-4 text-left transition-colors",
        "backdrop-blur-md",
        dimmed ? "opacity-50" : "opacity-100",
        locked && !selected ? "cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
      style={{
        borderColor: selected
          ? `hsl(${activeAccent} / 0.9)`
          : "hsl(0 0% 100% / 0.12)",
        background: selected
          ? `linear-gradient(135deg, hsl(${activeAccent} / 0.18), hsl(228 45% 12% / 0.7))`
          : "hsl(228 45% 12% / 0.55)",
        boxShadow: selected
          ? `0 0 0 1px hsl(${activeAccent} / 0.4), 0 18px 40px -22px hsl(${activeAccent} / 0.6)`
          : "0 12px 30px -22px hsl(230 60% 4% / 0.6)",
      }}
    >
      {/* selected pulse edge */}
      {selected && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-4 top-0 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, hsl(${activeAccent} / 0.8), transparent)`,
          }}
        />
      )}

      <div className="flex items-start gap-3">
        {icon && (
          <div
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border"
            style={{
              borderColor: `hsl(${activeAccent} / 0.35)`,
              background: `hsl(${activeAccent} / 0.08)`,
              color: `hsl(${activeAccent})`,
            }}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-heading text-sm font-semibold leading-tight text-white sm:text-base">
              {title}
            </h4>
            {selected && (
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                style={{
                  background: `hsl(${activeAccent} / 0.25)`,
                  color: `hsl(${activeAccent})`,
                }}
              >
                <Check className="h-3.5 w-3.5" />
              </span>
            )}
            {locked && !selected && (
              <Lock className="h-3.5 w-3.5 text-white/30" />
            )}
          </div>

          {description && (
            <p className="mt-1 text-xs leading-relaxed text-white/70 sm:text-[13px]">
              {description}
            </p>
          )}

          {immediateEffect && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-1 font-mono text-[11px] text-white/85">
              <TrendingUp className="h-3 w-3" />
              {immediateEffect}
            </div>
          )}

          {tradeoff && (
            <div
              className="mt-2 flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11px] leading-snug"
              style={{
                borderColor: "hsl(38 95% 55% / 0.35)",
                background: "hsl(38 95% 32% / 0.12)",
                color: "hsl(38 95% 75%)",
              }}
            >
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{tradeoff}</span>
            </div>
          )}

          {(risk || reward) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {risk && (
                <span
                  className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide"
                  style={{
                    borderColor: `hsl(${RISK_COLOR[risk]} / 0.5)`,
                    background: `hsl(${RISK_COLOR[risk]} / 0.12)`,
                    color: `hsl(${RISK_COLOR[risk]})`,
                  }}
                >
                  <ShieldAlert className="h-2.5 w-2.5" /> Risk · {risk}
                </span>
              )}
              {reward && (
                <span
                  className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide"
                  style={{
                    borderColor: `hsl(${RISK_COLOR[reward]} / 0.5)`,
                    background: `hsl(${RISK_COLOR[reward]} / 0.12)`,
                    color: `hsl(${RISK_COLOR[reward]})`,
                  }}
                >
                  <TrendingUp className="h-2.5 w-2.5" /> Reward · {reward}
                </span>
              )}
            </div>
          )}

          {children && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </motion.button>
  );
};

export default LunimarDecisionCard;